/** Import types */
import type {DesignCenterTabProps} from "../Interfaces.tsx";
import type {
    TimeSeries,
    TimeSeriesInput,
    TimeSeriesRow,
    TimeSeriesRowInput,
    UUID,
} from "../../base/ui_general/GeneralInterfaces.tsx";

import {RBSCard, RBSCardVariants} from "../../base/rbs_components/RBSCard";
import {
    BaseComponent,
    type BaseComponentState,
    BaseComponentStateInitial
} from "../../base/ui_general/BaseComponent.tsx";
import {createSuccessFeedbackMessage} from "../../base/utils/GeneralUtilities";
import {TimeSeriesRowDetailModal} from "../modals/TimeSeriesRowDetailModal.tsx";
import {TimeSeriesDetailModal} from "../modals/TimeSeriesDetailModal.tsx";
import {InputStateControl} from "../../base/utils/InputStateControl";
import {RBSButton, RBSButtonVariants, RBSButtonSizes} from "../../base/rbs_components/RBSButton";
import {
    getRequest_create_time_series,
    getRequest_update_time_series
} from "../../api/time_series.tsx";
import {
    getRequest_create_time_series_row,
    getRequest_update_time_series_row,
    getRequest_delete_time_series_row
} from "../../api/time_series_row.tsx";
import {ApiEndPoints, LabelTextMessageTypes, TimeSeriesTypeEnum} from "../../base/Settings";
import autobind from "autobind-decorator";
import Accordion from 'react-bootstrap/Accordion';
import Plot from 'react-plotly.js';
import React from 'react';
import styles from './TabShared.module.css';
import {TabCardHeader} from './components/TabCardHeader';
import {EmptyStateRow} from './components/EmptyStateRow';


/**
 * Props for the TimeSeriesTab component.
 */
interface TimeSeriesTabProps extends DesignCenterTabProps {
    /** Array of time series objects */
    time_series: Array<TimeSeries>;
}

/**
 * State for the TimeSeriesTab component.
 */
interface TimeSeriesTabState extends BaseComponentState {
    /** Whether the TimeSeries modal is shown */
    showTimeSeriesModal: boolean;
    /** Whether the TimeSeries modal is valid */
    timeSeriesModalValid: boolean;
    /** The TimeSeries being edited, if any */
    editingTimeSeries: TimeSeries | undefined;
    /** The currently active TimeSeries, if any */
    activeTimeSeries: TimeSeries | undefined;
    /** Whether the TimeSeriesRow modal is shown */
    showTimeSeriesRowModal: boolean;
    /** Whether the TimeSeriesRow modal is valid */
    timeSeriesRowModalValid: boolean;
    /** The TimeSeriesRow being edited, if any */
    editingTimeSeriesRow: TimeSeriesRow | undefined;
    /** Currently hovered row index for hover styling (undefined means no row hovered) */
    hoveredRow: number | undefined;
}


/**
 * TimeSeriesTab Component
 */
export class TimeSeriesTab extends BaseComponent<TimeSeriesTabProps, TimeSeriesTabState> {
    private readonly timeSeriesInputStateControl: InputStateControl;
    private readonly timeSeriesRowInputStateControl: InputStateControl;

    constructor(props: TimeSeriesTabProps) {
        super(props);

        this.state = {
            ...BaseComponentStateInitial,
            showTimeSeriesModal: false,
            timeSeriesModalValid: true,
            editingTimeSeries: undefined,
            activeTimeSeries: undefined,

            showTimeSeriesRowModal: false,
            timeSeriesRowModalValid: true,
            editingTimeSeriesRow: undefined,
            hoveredRow: undefined
        };

        this.timeSeriesInputStateControl = new InputStateControl();
        this.timeSeriesInputStateControl.setGlobalValidityUpdateCallback(this.handleTimeSeriesModalValidityUpdate);

        this.timeSeriesRowInputStateControl = new InputStateControl();
        this.timeSeriesRowInputStateControl.setGlobalValidityUpdateCallback(this.handleTimeSeriesRowModalValidityUpdate);
    }

    /** TimeSeries modal validity update */
    @autobind
    handleTimeSeriesModalValidityUpdate(isValid: boolean) {
        this.setState({timeSeriesModalValid: isValid});
    }

    /** TimeSeriesRow modal validity update */
    @autobind
    handleTimeSeriesRowModalValidityUpdate(isValid: boolean) {
        this.setState({timeSeriesRowModalValid: isValid});
    }

    /** Submit handler for TimeSeries modal */
    @autobind
    handleTimeSeriesModalSubmit(input: Partial<TimeSeriesInput>) {  //TODO: Why do we need Partial here? Please avoid
        if (!this.props.transaction || !this.props.transaction.id) return;

        const payload: TimeSeriesInput = {
            ...input,
            transaction_id: this.props.transaction.id,
            name: input.name || "",
            description: input.description || "",
            type: input.type || TimeSeriesTypeEnum.OTHER
        };

        //TODO: Why do we need to create a dummy row here? Can't we create a TimeSeries without rows?
        const dummyRow: TimeSeriesRowInput = {
            time_series_id: "",
            ref_date: new Date().toISOString().split("T")[0],
            value: 0
        };

        if (this.state.editingTimeSeries) {
            //TODO: Move the stripping of time_series_rows into the corresponding getFetchRequest... routine
            //TODO: The same applies to TimeSeries, Triggers, Waterfalls, Formulas...

            const {time_series_rows, ...cleanedEditing} = this.state.editingTimeSeries;

            const updated: TimeSeries = {
                ...cleanedEditing,
                ...payload,
                time_series_rows: []
            };

            this.processFetchRequest(
                getRequest_update_time_series(updated),
                () => {
                    this.props.displayMessage(createSuccessFeedbackMessage(LabelTextMessageTypes.TIMESERIES_UPDATED));
                    this.props.loadData([ApiEndPoints.TIME_SERIES]);
                    this.setState({showTimeSeriesModal: false, editingTimeSeries: undefined});
                }
            );
        } else {
            this.processFetchRequest(
                getRequest_create_time_series(payload),
                (createdTimeSeries: TimeSeries) => {
                    this.processFetchRequest(
                        getRequest_create_time_series_row({
                            ...dummyRow,
                            time_series_id: createdTimeSeries.id
                        }),
                        () => {
                            this.props.displayMessage(createSuccessFeedbackMessage(LabelTextMessageTypes.TIMESERIES_CREATED));
                            this.props.loadData([ApiEndPoints.TIME_SERIES]);
                            this.setState({showTimeSeriesModal: false});
                        }
                    );
                }
            );
        }
    }

    /** Submit handler for TimeSeriesRow modal */
    @autobind
    handleTimeSeriesRowModalSubmit(input: TimeSeriesRowInput) {
        if (!this.state.activeTimeSeries) return;

        input.time_series_id = this.state.activeTimeSeries.id;

        if (this.state.editingTimeSeriesRow) {
            const updated = {...this.state.editingTimeSeriesRow, ...input};
            this.processFetchRequest(
                getRequest_update_time_series_row(updated),
                () => {
                    this.props.displayMessage(createSuccessFeedbackMessage(LabelTextMessageTypes.TIMESERIES_ROW_UPDATED));
                    this.props.loadData([ApiEndPoints.TIME_SERIES]);
                    this.setState({showTimeSeriesRowModal: false, editingTimeSeriesRow: undefined});
                }
            );
        } else {
            this.processFetchRequest(
                getRequest_create_time_series_row(input),
                () => {
                    this.props.displayMessage(createSuccessFeedbackMessage(LabelTextMessageTypes.TIMESERIES_ROW_CREATED));
                    this.props.loadData([ApiEndPoints.TIME_SERIES]);
                    this.setState({showTimeSeriesRowModal: false, editingTimeSeriesRow: undefined});
                }
            );
        }
    }

    /**
     * Handles the deletion of a time series row by its UUID via an API request.
     * @param time_series_row_id The UUID of the time series row to delete.
     */
    @autobind
    handleDeleteTimeSeriesRow(time_series_row_id: UUID) {
        this.processFetchRequest(
            getRequest_delete_time_series_row(time_series_row_id),
            () => {
                this.props.displayMessage(createSuccessFeedbackMessage(LabelTextMessageTypes.TIMESERIES_DELETED));
                this.props.loadData([ApiEndPoints.TIME_SERIES]);
            }
        );
    }


    render() {
        // Helper to check if a string is a valid ISO date
        function isValidISODate(date: string | undefined): boolean {
            if (!date) return false;
            const d = new Date(date);
            return !isNaN(d.getTime()) && /^\d{4}-\d{2}-\d{2}/.test(date);
        }

        // Get effective_date and maturity_date from transaction (if available)
        const effectiveDate = isValidISODate(this.props.transaction?.effective_date) ? this.props.transaction?.effective_date : undefined;
        const maturityDate = isValidISODate(this.props.transaction?.maturity_date) ? this.props.transaction?.maturity_date : undefined;


        // Helper to sort rows by ref_date ascending
        function sortRowsByDate(rows: Array<any>) {
            return [...rows].sort((a, b) => (a.ref_date < b.ref_date ? -1 : a.ref_date > b.ref_date ? 1 : 0));
        }

        // Responsive Plotly: functional component for responsive Plotly plot
        function ResponsivePlotly({data, layout, ...rest}: any) {
            const plotContainerRef = React.useRef<HTMLDivElement>(null);
            const [containerWidth, setContainerWidth] = React.useState<number | undefined>(undefined);
            React.useEffect(() => {
                if (!plotContainerRef.current) return;
                const observer = new window.ResizeObserver(entries => {
                    for (const entry of entries) {
                        if (entry.contentRect) {
                            setContainerWidth(entry.contentRect.width);
                        }
                    }
                });
                observer.observe(plotContainerRef.current);
                setContainerWidth(plotContainerRef.current.offsetWidth);
                return () => observer.disconnect();
            }, []);
            return (
                <div ref={plotContainerRef} className={styles.plotContainer}>
                    <Plot
                        data={data}
                        layout={{...layout, width: containerWidth}}
                        config={{displayModeBar: true, responsive: true}}
                        useResizeHandler={false}
                        className={styles.plotFill}
                        {...rest}
                    />
                </div>
            );
        }

        return (
            <div className={styles.tabContainer}>
                <div className={styles.scrollArea}>
                    {/* Render each TimeSeries as an RBSCard */}
                    <div className={styles.cardStack}>
                        {this.props.time_series.map((ts, idx) => {
                                const sortedOriginalRows = sortRowsByDate(ts.time_series_rows || []);
                                const sortedInterpolatedRows = sortRowsByDate(ts.interpolated_rows || []);

                                return (
                                    <RBSCard
                                        key={idx}
                                        header={
                                            <TabCardHeader
                                                icon={"📈"}
                                                title={<span
                                                    className={styles.cardHeaderTitle}>Time Series: {ts.name}</span>}
                                                actions={<>
                                                    <RBSButton
                                                        label="Add Row"
                                                        onClick={() =>
                                                            this.setState({
                                                                showTimeSeriesRowModal: true,
                                                                editingTimeSeriesRow: undefined,
                                                                activeTimeSeries: ts
                                                            })
                                                        }
                                                        variant={RBSButtonVariants.PRIMARY}
                                                        size={RBSButtonSizes.SMALL}
                                                        extraStyle={{marginLeft: "1rem"}}
                                                    />
                                                    <RBSButton
                                                        label="Edit"
                                                        onClick={() =>
                                                            this.setState({
                                                                showTimeSeriesModal: true,
                                                                editingTimeSeries: ts
                                                            })
                                                        }
                                                        variant={RBSButtonVariants.SECONDARY}
                                                        size={RBSButtonSizes.SMALL}
                                                        extraStyle={{marginLeft: "0.5rem"}}
                                                    />
                                                </>}
                                            />
                                        }
                                        header_class_name={styles.cardHeaderPrimary}
                                        background={RBSCardVariants.Light}
                                        border={RBSCardVariants.Primary}
                                        body_class_name={styles.cardBodyPanel}
                                        container_class_name={styles.roundedCard}
                                    >
                                        {/* Responsive Plotly plot */}
                                        <ResponsivePlotly
                                            data={[
                                                {
                                                    x: sortedOriginalRows.map(row => row.ref_date),
                                                    y: sortedOriginalRows.map(row => row.value),
                                                    type: 'scatter',
                                                    mode: 'lines+markers',
                                                    name: 'Original Data',
                                                    marker: {
                                                        color: '#1f77b4',
                                                        size: 8,
                                                        line: {color: '#fff', width: 2},
                                                        symbol: 'circle'
                                                    },
                                                    line: {color: '#1f77b4', width: 3},
                                                    hovertemplate: 'Date: %{x}<br>Value: %{y:.4f}<extra></extra>'
                                                },
                                                {
                                                    x: sortedInterpolatedRows.map(row => row.ref_date),
                                                    y: sortedInterpolatedRows.map(row => row.value),
                                                    type: 'scatter',
                                                    mode: 'lines',
                                                    name: 'Interpolated',
                                                    line: {color: '#ff7f0e', dash: 'dot', width: 3},
                                                    hovertemplate: 'Date: %{x}<br>Interpolated: %{y:.4f}<extra></extra>'
                                                }
                                            ]}
                                            layout={{
                                                autosize: true,
                                                height: 300,
                                                margin: {l: 60, r: 30, t: 70, b: 60},
                                                title: {
                                                    text: `Time Series: ${ts.name}`,
                                                    font: {family: 'Segoe UI, Arial, sans-serif', size: 20, color: '#222'},
                                                    xref: 'paper',
                                                    x: 0.02,
                                                    y: 0.95,
                                                    xanchor: 'left',
                                                    yanchor: 'top',
                                                    pad: {t: 10, b: 10}
                                                },
                                                xaxis: {
                                                    title: {
                                                        text: 'Date',
                                                        font: {
                                                            family: 'Segoe UI, Arial, sans-serif',
                                                            size: 16,
                                                            color: '#444'
                                                        }
                                                    },
                                                    type: 'date',
                                                    tickformat: '%Y-%m-%d',
                                                    showgrid: true,
                                                    gridcolor: '#e3e8ee',
                                                    zeroline: false,
                                                    linecolor: '#b0bec5',
                                                    linewidth: 1.5,
                                                    ticks: 'outside',
                                                    tickfont: {
                                                        family: 'Segoe UI, Arial, sans-serif',
                                                        size: 13,
                                                        color: '#555'
                                                    },
                                                    range: effectiveDate && maturityDate ? [effectiveDate, maturityDate] : undefined
                                                },
                                                yaxis: {
                                                    title: {
                                                        text: 'Value',
                                                        font: {
                                                            family: 'Segoe UI, Arial, sans-serif',
                                                            size: 16,
                                                            color: '#444'
                                                        }
                                                    },
                                                    showgrid: true,
                                                    gridcolor: '#e3e8ee',
                                                    zeroline: false,
                                                    linecolor: '#b0bec5',
                                                    linewidth: 1.5,
                                                    ticks: 'outside',
                                                    tickfont: {
                                                        family: 'Segoe UI, Arial, sans-serif',
                                                        size: 13,
                                                        color: '#555'
                                                    }
                                                },
                                                legend: {
                                                    orientation: 'v',
                                                    y: 0.98,
                                                    yanchor: 'top',
                                                    x: 0.98,
                                                    xanchor: 'right',
                                                    bgcolor: 'rgba(255,255,255,0.85)',
                                                    bordercolor: '#b0bec5',
                                                    borderwidth: 1,
                                                    font: {family: 'Segoe UI, Arial, sans-serif', size: 14, color: '#333'}
                                                },
                                                plot_bgcolor: 'linear-gradient(180deg, #f7fafc 0%, #e3e8ee 100%)',
                                                paper_bgcolor: '#f7fafc',
                                                font: {family: 'Segoe UI, Arial, sans-serif', size: 13, color: '#222'},
                                                transition: {duration: 400, easing: 'cubic-in-out'}
                                            }}
                                        />
                                        {/* Accordion containing the table */
                                        }
                                        <Accordion defaultActiveKey="" alwaysOpen={false}>
                                            <Accordion.Item eventKey="0">
                                                <Accordion.Header>Data Table</Accordion.Header>
                                                 <Accordion.Body>
                                                    <table className={`${styles.dataTable} ${styles.tableMarginLg}`}
                                                            aria-label={`Time series data for ${ts.name}`}>
                                                        <thead>
                                                        <tr>
                                                            <th className={styles.headerPadLg}>Reference Date</th>
                                                            <th className={styles.headerPadLg}>Value</th>
                                                            <th className={`${styles.headerActionsNarrow} ${styles.headerPadLg}`}>{/* actions */}</th>
                                                        </tr>
                                                        </thead>
                                                        <tbody>
                                                        {(ts.time_series_rows && ts.time_series_rows.length > 0 ? sortRowsByDate(ts.time_series_rows) : []).map((row, rIdx) => (
                                                            <tr
                                                                key={rIdx}
                                                                className={this.state.hoveredRow === rIdx ? styles.tableRowHover : ''}
                                                                onMouseEnter={() => this.setState({hoveredRow: rIdx})}
                                                                onMouseLeave={() => this.setState({hoveredRow: undefined})}
                                                            >
                                                                <td className={`${styles.cellPadLg} ${styles.cellCenter}`}>{row.ref_date}</td>
                                                                <td className={`${styles.cellPadLg} ${styles.cellCenter}`}>{row.value !== undefined && row.value !== null ? row.value.toFixed(4) : ''}</td>
                                                                <td className={`${styles.actionCell} ${styles.cellPadLg}`}>
                                                                    <div className={styles.hoverButtons}>
                                                                        <RBSButton
                                                                            label="✏️"
                                                                            title="Edit"
                                                                            onClick={() =>
                                                                                this.setState({
                                                                                    showTimeSeriesRowModal: true,
                                                                                    editingTimeSeriesRow: row,
                                                                                    activeTimeSeries: ts
                                                                                })
                                                                            }
                                                                            variant={RBSButtonVariants.SECONDARY}
                                                                            size={RBSButtonSizes.SMALL}
                                                                        />
                                                                        <RBSButton
                                                                            label="🗑️"
                                                                            title="Delete"
                                                                            onClick={() => {
                                                                                this.handleDeleteTimeSeriesRow(row.id);
                                                                            }}
                                                                            variant={RBSButtonVariants.DANGER}
                                                                            size={RBSButtonSizes.SMALL}
                                                                        />
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                        {(!ts.time_series_rows || ts.time_series_rows.length === 0) && (
                                                            <EmptyStateRow inTable colSpan={3}
                                                                           message={'No rows yet. Use "Add Row" to get started.'}/>
                                                        )}
                                                        </tbody>
                                                    </table>
                                                </Accordion.Body>
                                            </Accordion.Item>
                                        </Accordion>
                                    </RBSCard>
                                )
                                    ;
                            }
                        )
                        }
                    </div>

                    <div className={styles.toolbarBottom}>
                        <RBSButton
                            label="Add Time Series"
                            variant={RBSButtonVariants.PRIMARY}
                            size={RBSButtonSizes.LARGE}
                            onClick={() => this.setState({showTimeSeriesModal: true, editingTimeSeries: undefined})}
                            title="Add Portfolio"
                        />
                    </div>

                    {/* TimeSeries modal */}
                    <TimeSeriesDetailModal
                        show={this.state.showTimeSeriesModal}
                        timeSeries={this.state.editingTimeSeries}
                        onSubmit={this.handleTimeSeriesModalSubmit}
                        onClose={() => this.setState({showTimeSeriesModal: false, editingTimeSeries: undefined})}
                        isValid={this.state.timeSeriesModalValid}
                        inputStateControl={this.timeSeriesInputStateControl}
                        user_language={this.props.user_language}
                    />

                    {/* TimeSeriesRow modal */}
                    <TimeSeriesRowDetailModal
                        show={this.state.showTimeSeriesRowModal}
                        timeSeriesRow={this.state.editingTimeSeriesRow}
                        onSubmit={this.handleTimeSeriesRowModalSubmit}
                        onClose={() => this.setState({showTimeSeriesRowModal: false})}
                        isValid={this.state.timeSeriesRowModalValid}
                        inputStateControl={this.timeSeriesRowInputStateControl}
                        user_language={this.props.user_language}
                        currentTimeSeriesId={this.state.activeTimeSeries?.id ?? ""}
                    />
                </div>
            </div>
        );
    }
}
